import {
  Alert,
  Button,
  Checkbox,
  ListItemText,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import { useSaveServiceTerms, useServiceTerms, useServiceTypes } from '@/api/hooks'
import type { TermClause } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { TermsDisplayRules } from '@/pages/settings/TermsDisplayRules'
import { ErrorBanner, humanise } from '@/components/common'

const TERM_TYPES = ['PAYMENT', 'CANCELLATION', 'GENERAL'] as const

const BLANK: TermClause = { termType: 'GENERAL', documentTypes: [], content: '' }

/**
 * The documents a clause can be sent to. Choosing none means every one, which
 * is what most clauses want - the exceptions are the ones that belong to a
 * particular document, or to two of them and no others.
 */
const DOCUMENT_TYPES = [
  { value: 'QUOTATION', label: 'Quotation' },
  { value: 'ORDER_CONFIRMATION', label: 'Order confirmation' },
  { value: 'PROFORMA', label: 'Proforma invoice' },
  { value: 'INVOICE', label: 'Tax invoice' },
  { value: 'RECEIPT', label: 'Receipt' },
  { value: 'MENU_SHEET', label: 'Menu sheet' },
] as const

const documentLabel = (value: string) =>
  DOCUMENT_TYPES.find((document) => document.value === value)?.label ?? value

/**
 * The terms a quotation carries, written per line of business.
 *
 * <p>A banquet holds a hall and carries hall rules; an outdoor job carries
 * travel, water and site access; a bulk takeaway carries neither. Clauses under
 * "All services" go on every quotation whatever is being sold.</p>
 */
export function TermsTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data, error } = useServiceTerms()
  // Every line of business this company sells, named. Switched-off ones
  // included: terms are written once and used whenever the service is on, and a
  // counter sale that is off today is exactly the case that carried none.
  const { data: services } = useServiceTypes({ includeInactive: true })
  // Taken from the answer itself rather than worked out again here: the server
  // decides which lines of business terms may be written for, and a screen with
  // its own opinion is how a counter sale came to have none.
  const serviceTypes = (data ?? []).map((entry) => entry.serviceType)
  /** What the business calls it, without saying whether it is switched on. */
  const serviceName = (code: string) =>
    code === 'ALL'
      ? 'All services'
      : ((services ?? []).find((candidate) => candidate.code === code)?.name ?? humanise(code))

  /** The tab label, which does say - so nobody wonders why it is not selling. */
  const serviceLabel = (code: string) => {
    const service = (services ?? []).find((candidate) => candidate.code === code)
    return service && !service.active ? `${serviceName(code)} (off)` : serviceName(code)
  }
  const save = useSaveServiceTerms()
  const [serviceType, setServiceType] = useState<string>('ALL')
  const [clauses, setClauses] = useState<TermClause[]>([])
  const [dirty, setDirty] = useState(false)

  const writable = canWrite('config')
  const saved = data?.find((entry) => entry.serviceType === serviceType)?.terms ?? []

  // Load the chosen service's clauses, and leave unsaved edits alone.
  useEffect(() => {
    setClauses(saved.map((clause) => ({ ...clause })))
    setDirty(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType, data])

  const update = (index: number, patch: Partial<TermClause>) => {
    setClauses((current) => current.map((clause, at) => (at === index ? { ...clause, ...patch } : clause)))
    setDirty(true)
  }

  const move = (index: number, by: number) => {
    const target = index + by
    if (target < 0 || target >= clauses.length) return
    setClauses((current) => {
      const next = [...current]
      const [held] = next.splice(index, 1)
      next.splice(target, 0, held)
      return next
    })
    setDirty(true)
  }

  const usable = clauses.filter((clause) => clause.content.trim() !== '')

  // What the server would actually find for this document: the saved clauses
  // under "All services" plus the ones under the chosen service, the same two
  // sets printedTermsFor reads. Unsaved edits are left out on purpose - a code
  // switched on here points at what is live, not at what is still being typed.
  const savedAll = data?.find((entry) => entry.serviceType === 'ALL')?.terms ?? []
  const savedService = serviceType === 'ALL' ? [] : saved
  const hasClause = (document: string) =>
    [...savedAll, ...savedService].some(
      (clause) =>
        clause.content.trim() !== ''
        && (!clause.documentTypes || clause.documentTypes.length === 0
          || clause.documentTypes.includes(document)),
    )

  return (
    <>
      <ErrorBanner error={error ?? save.error} />

      <Tabs
        value={serviceType}
        onChange={(_event, value: string) => setServiceType(value)}
        variant="scrollable"
        sx={{ mb: 2 }}
      >
        {serviceTypes.map((type) => (
          <Tab key={type} value={type} label={serviceLabel(type)} />
        ))}
      </Tabs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {serviceType === 'ALL'
            ? 'Carried on every quotation, whatever is being sold. Written first, above the clauses for the service.'
            : `Carried on a ${serviceName(serviceType).toLowerCase()} document, after the clauses under All services.`}
        </Typography>
        {writable ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setClauses((current) => [...current, { ...BLANK }])
              setDirty(true)
            }}
          >
            Add clause
          </Button>
        ) : null}
      </Stack>

      {clauses.length === 0 ? (
        <Alert severity="info">
          No clause written here. Quotations carry the standard wording the system generates until one is.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {clauses.map((clause, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
              <TextField
                select
                size="small"
                label="Kind"
                value={clause.termType}
                onChange={(event) => update(index, { termType: event.target.value })}
                sx={{ width: 190 }}
                disabled={!writable}
                inputProps={{ 'aria-label': `Kind ${index + 1}` }}
              >
                {TERM_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {humanise(type)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={`Clause ${index + 1}`}
                value={clause.content}
                onChange={(event) => update(index, { content: event.target.value })}
                disabled={!writable}
              />
              {/* After the clause: you write the sentence, then say where it
                  goes. Several documents can take the same one - the payment
                  wording belongs on the proforma and the tax invoice both. */}
              <TextField
                select
                size="small"
                label="Prints on"
                value={clause.documentTypes ?? []}
                onChange={(event) =>
                  update(index, {
                    documentTypes: typeof event.target.value === 'string'
                      ? event.target.value.split(',')
                      : (event.target.value as unknown as string[]),
                  })
                }
                sx={{ width: 230 }}
                disabled={!writable}
                // "Every document" stands in for an empty choice, so the label
                // has to sit up on the border whether anything is picked or not.
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const chosen = selected as string[]
                    return chosen.length === 0
                      ? 'Every document'
                      : chosen.map(documentLabel).join(', ')
                  },
                }}
                inputProps={{ 'aria-label': `Prints on ${index + 1}` }}
              >
                {DOCUMENT_TYPES.map((document) => (
                  <MenuItem key={document.value} value={document.value}>
                    <Checkbox
                      size="small"
                      checked={(clause.documentTypes ?? []).includes(document.value)}
                    />
                    <ListItemText primary={document.label} />
                  </MenuItem>
                ))}
              </TextField>
              {writable ? (
                <Stack>
                  <IconButton
                    size="small"
                    aria-label={`Move clause ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move clause ${index + 1} down`}
                    disabled={index === clauses.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Remove clause ${index + 1}`}
                    onClick={() => {
                      setClauses((current) => current.filter((_, at) => at !== index))
                      setDirty(true)
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : null}
            </Stack>
          ))}
        </Stack>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        Leave <strong>Prints on</strong> empty and the clause goes on every document; name one or
        more and it goes only there — the hall rules on the quotation and the confirmation, the
        payment wording on the proforma and the tax invoice.
        Write <code>{'{advancePercent}'}</code> into a clause and the figure is filled in when the
        quotation is raised, so the wording cannot drift from what is actually collected. Quotations
        already raised keep the words they were raised on.
      </Alert>

      {writable ? (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button
            disabled={!dirty}
            onClick={() => {
              setClauses(saved.map((clause) => ({ ...clause })))
              setDirty(false)
            }}
          >
            Discard changes
          </Button>
          <Button
            variant="contained"
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  serviceType,
                  terms: usable.map((clause) => ({
                    termType: clause.termType,
                    documentTypes: clause.documentTypes ?? [],
                    content: clause.content.trim(),
                  })),
                },
                { onSuccess: () => setDirty(false) },
              )
            }
          >
            Save terms
          </Button>
        </Stack>
      ) : null}

      {/* Written above, carried below. The same screen, because a clause and
          the way it travels are one decision made at one sitting. */}
      <TermsDisplayRules serviceType={serviceType} writable={writable} hasClause={hasClause} />
    </>
  )
}
